import { Col, Form, Row, Space } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FormResponseType, FormSharing, FormTemplate } from 'api/form/types';
import { Radio as RadioAtom } from 'components/atoms/radio';
import { Page } from 'components/molecules/page';
import { Caption01, ProductiveHeading07 } from 'components/styles';
import { useAccountDataQuery } from 'queries/account';
import { useFormTemplateUpdateMutation } from 'queries/form-template';

const Wrapper = styled.div`
  width: 928px;
  margin: 50px auto;
`;

const Label = styled.div`
  ${ProductiveHeading07};
`;
const Description = styled.div`
  ${Caption01};
  color: var(--gray-100);
  margin-top: var(--spacing-01);
`;

const RadioGroup = styled(RadioAtom.Group)`
  margin-top: var(--spacing-04);
`;
const Radio = styled(RadioAtom)`
  margin: 0 0 var(--spacing-06);
  .ant-radio {
    margin-top: var(--spacing-01);
  }
`;

export const FormSettings: FC<{ formTemplate: FormTemplate }> = ({
  formTemplate,
}) => {
  const [form] = Form.useForm();
  const { mutate } = useFormTemplateUpdateMutation(formTemplate.templateId);
  const triggerOnChange = () => {
    mutate({ formSettings: form.getFieldsValue() });
  };
  const { data: accountData } = useAccountDataQuery();

  return (
    <Page>
      <Page.Content>
        <Wrapper>
          <Form
            form={form}
            name="form_settings_configuration"
            autoComplete="off"
            initialValues={{
              formResponseType: formTemplate.formSettings.formResponseType,
              formSharing: formTemplate.formSettings.formSharing,
            }}
            preserve={false}
            layout="vertical"
          >
            <Label>
              <FormattedMessage id="form_templates.settings.responses" />
            </Label>
            <Description>
              <FormattedMessage id="form_templates.settings.response.desc" />
            </Description>
            <Form.Item name="formResponseType">
              <RadioGroup
                name="formResponseType"
                onChange={() => triggerOnChange()}
              >
                <Row gutter={[16, 0]}>
                  <Col span={24}>
                    <Radio value={FormResponseType.MultipleResponse}>
                      <Space direction="vertical" size={0}>
                        <FormattedMessage id="form_templates.settings.multiple_response" />
                        <Description>
                          <FormattedMessage id="form_templates.settings.multiple_response.desc" />
                        </Description>
                      </Space>
                    </Radio>
                  </Col>
                  <Col>
                    <Radio value={FormResponseType.SingleResponse}>
                      <Space direction="vertical" size={0}>
                        <FormattedMessage id="form_templates.settings.single_response" />
                        <Description>
                          <FormattedMessage id="form_templates.settings.single_response.desc" />
                        </Description>
                      </Space>
                    </Radio>
                  </Col>
                </Row>
              </RadioGroup>
            </Form.Item>

            <Label>
              <FormattedMessage id="form_templates.settings.sharing" />
            </Label>
            <Form.Item name="formSharing">
              <RadioGroup name="formSharing" onChange={() => triggerOnChange()}>
                <Row gutter={[16, 0]}>
                  <Col span={24}>
                    <Radio value={FormSharing.Restricted}>
                      <Space direction="vertical" size={0}>
                        <FormattedMessage id="form_templates.settings.sharing.restricted" />
                        <Description>
                          <FormattedMessage
                            id="form_templates.settings.sharing.restricted.desc"
                            values={{
                              domainName: (
                                <strong>
                                  {accountData?.account.primaryDomainName}
                                </strong>
                              ),
                            }}
                          />
                        </Description>
                      </Space>
                    </Radio>
                  </Col>
                  <Col span={24}>
                    <Radio value={FormSharing.Anyone}>
                      <Space direction="vertical" size={0}>
                        <FormattedMessage id="form_templates.settings.sharing.anyone" />
                        <Description>
                          <FormattedMessage id="form_templates.settings.sharing.anyone.desc" />
                        </Description>
                      </Space>
                    </Radio>
                  </Col>
                </Row>
              </RadioGroup>
            </Form.Item>
          </Form>
        </Wrapper>
      </Page.Content>
    </Page>
  );
};
